import { useState } from 'react';
import { Badge, Button, Card, Group, Menu, SegmentedControl, Stack, Text } from '@mantine/core';
import { IconCheck, IconClock, IconDots, IconEye, IconRefresh } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import dayjs from 'dayjs';
import { api } from '../api';
import { avisarError, avisarOk, Cabecera, fechaCorta } from '../comun';
import type { Aviso } from '../tipos';

const COLOR = { urgente: 'red', atencion: 'orange', info: 'blue' } as const;
const NIVEL = { urgente: 'Urgente', atencion: 'Atención', info: 'Información' } as const;

export function PaginaAvisos() {
  const qc = useQueryClient();
  const [vista, setVista] = useState('abiertos');
  const lista = useQuery({ queryKey: ['avisos', vista], queryFn: () => api.get<Aviso[]>(`/avisos?estado=${vista}`) });
  const [generando, setGenerando] = useState(false);

  const generar = async () => {
    setGenerando(true);
    try {
      const r = await api.post<{ nuevos: number; resueltos: number; emailsEnviados: number; telegramEnviados: number; esDemo: boolean }>('/avisos/generar');
      const fuera = [r.telegramEnviados ? `${r.telegramEnviados} enviados por Telegram` : '', r.emailsEnviados ? `${r.emailsEnviados} por correo` : ''].filter(Boolean).join(', ');
      avisarOk(`${r.nuevos} nuevos, ${r.resueltos} resueltos${fuera ? `, ${fuera}` : ''}${r.esDemo ? '. (Demostración: no se envía nada fuera)' : ''}`, 'Avisos revisados');
      await qc.invalidateQueries({ queryKey: ['avisos'] });
    } catch (e) {
      avisarError(e);
    } finally {
      setGenerando(false);
    }
  };

  const cambiar = async (a: Aviso, estado: string, pospuestoHasta?: string) => {
    try {
      await api.post(`/avisos/${a.id}/estado`, { estado, pospuestoHasta });
      await qc.invalidateQueries({ queryKey: ['avisos'] });
    } catch (e) {
      avisarError(e);
    }
  };

  return (
    <>
      <Cabecera titulo="Avisos" descripcion="Plazos, vencimientos, justificantes pendientes y controles. Se revisan solos cada mañana.">
        <SegmentedControl
          value={vista}
          onChange={setVista}
          data={[
            { value: 'abiertos', label: 'Abiertos' },
            { value: 'todos', label: 'Todos' },
          ]}
        />
        <Button leftSection={<IconRefresh size={16} />} variant="light" loading={generando} onClick={generar}>
          Revisar ahora
        </Button>
      </Cabecera>
      <Stack>
        {(lista.data ?? []).map((a) => (
          <Card key={a.id} withBorder padding="sm" style={{ opacity: a.estado === 'resuelto' ? 0.55 : 1 }}>
            <Group justify="space-between" wrap="nowrap" align="flex-start">
              <Stack gap={4} style={{ minWidth: 0 }}>
                <Group gap="xs">
                  <Badge color={COLOR[a.nivel]} size="sm">
                    {NIVEL[a.nivel]}
                  </Badge>
                  <Text size="xs" c="dimmed">
                    {a.tipoNombre}
                  </Text>
                  {a.estado !== 'pendiente' && (
                    <Badge variant="outline" size="xs" color="gray">
                      {a.estado}
                      {a.pospuestoHasta ? ` hasta ${fechaCorta(a.pospuestoHasta)}` : ''}
                    </Badge>
                  )}
                </Group>
                <Text fw={600} size="sm">
                  {a.titulo}
                </Text>
                {a.detalle && (
                  <Text size="sm" c="dimmed">
                    {a.detalle}
                  </Text>
                )}
                {a.enlace && (
                  <Text size="sm" component={Link} to={a.enlace} c="blue">
                    Ir →
                  </Text>
                )}
              </Stack>
              <Menu position="bottom-end">
                <Menu.Target>
                  <Button variant="subtle" size="xs" px={6} aria-label="Acciones">
                    <IconDots size={16} />
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item leftSection={<IconEye size={14} />} onClick={() => void cambiar(a, 'visto')}>
                    Marcar como visto
                  </Menu.Item>
                  <Menu.Item leftSection={<IconClock size={14} />} onClick={() => void cambiar(a, 'pospuesto', dayjs().add(7, 'day').format('YYYY-MM-DD'))}>
                    Posponer una semana
                  </Menu.Item>
                  <Menu.Item leftSection={<IconCheck size={14} />} onClick={() => void cambiar(a, 'resuelto')}>
                    Dar por resuelto
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Card>
        ))}
        {lista.data?.length === 0 && (
          <Text c="dimmed" ta="center" p="lg">
            Todo en orden: no hay avisos.
          </Text>
        )}
      </Stack>
    </>
  );
}
